import { useMemo, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { FoodsIcon, Typography } from "@foodpilot/foods";
import {
    createMRTColumnHelper,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_SortingState,
} from "material-react-table";
import { useTranslation } from "react-i18next";

import { TransformationReferenceAdmin } from "src/api/sure-api/useTransformationReferencesAdmin";
import { PrimaryGrid } from "src/components/Grid/layout/PrimaryGrid";
import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";

type TransformationReferencesTableProps = {
    references: TransformationReferenceAdmin[];
    isLoading: boolean;
    isError: boolean;
    canManage: boolean;
    onRetry: () => void;
    onEdit: (reference: TransformationReferenceAdmin) => void;
    onDelete: (reference: TransformationReferenceAdmin) => void;
};

const columnHelper = createMRTColumnHelper<TransformationReferenceAdmin>();

export const TransformationReferencesTable = (props: TransformationReferencesTableProps) => {
    const { references, isLoading, isError, canManage, onRetry, onEdit, onDelete } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { isMobile } = useResponsiveBreakpoints();
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 20 });
    const [sorting, setSorting] = useState<MRT_SortingState>([]);

    const columns = useMemo(
        () => [
            columnHelper.accessor("code", {
                header: t("common.code", "Code"),
                size: isMobile ? 96 : 180,
                enableColumnFilter: false,
                enableSorting: false,
                Cell: ({ cell }) => (
                    <Typography noWrap variant="body-semibold" title={cell.getValue()}>
                        {cell.getValue()}
                    </Typography>
                ),
            }),
            columnHelper.accessor("name", {
                header: t("common.name", "Name"),
                size: isMobile ? 128 : 220,
                enableColumnFilter: false,
                enableSorting: false,
                Cell: ({ cell }) => (
                    <Typography noWrap title={cell.getValue()}>
                        {cell.getValue()}
                    </Typography>
                ),
            }),
            columnHelper.accessor((reference) => reference.ecobalyseProcessUuid ?? "–", {
                id: "ecobalyseProcessUuid",
                header: t("transformation.references.ecobalyse_uuid", "Ecobalyse process UUID"),
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor((reference) => reference.scores?.length ?? 0, {
                id: "scores",
                header: t("common.scores", "Scores"),
                enableColumnFilter: false,
                enableSorting: false,
            }),
        ],
        [isMobile, t],
    );
    const columnConfiguration = useGridColumns(columns, "transformation-references", { persist: false });
    const rowMenuItems = useMemo(
        () =>
            canManage ?
                [
                    {
                        icon: <FoodsIcon icon="edit" size={2} />,
                        label: t("common.actions.edit", "Modify"),
                        onClick: onEdit,
                    },
                    {
                        icon: <FoodsIcon icon="delete" size={2} />,
                        label: t("common.actions.delete", "Delete"),
                        onClick: onDelete,
                    },
                ]
            :   [],
        [canManage, onDelete, onEdit, t],
    );

    if (isError) {
        return (
            <Box sx={{ padding: theme.spacing(3) }}>
                <RemoteErrorAlert
                    message={t("transformation.references.load_error", "Unable to load transformation processes.")}
                    onRetry={onRetry}
                    status="server-error"
                />
            </Box>
        );
    }

    return (
        <PrimaryGrid
            appearance="hub"
            columns={columns}
            columnConfig={columnConfiguration}
            data={references}
            emptyMessage={t("transformation.references.empty", "No transformation process found.")}
            enableColumnResizing={false}
            enablePagination={false}
            enableRowActions={canManage}
            enableServerSideFiltering={false}
            enableServerSidePagination={false}
            enableServerSideSorting={false}
            enableViews={false}
            filters={{ columnFilters, setColumnFilters }}
            getRowActionLabel={(reference) => `${t("common.actions", "Actions")} ${reference.name}`}
            gridKey="transformation-references"
            isLoading={isLoading}
            menuItems={rowMenuItems}
            mobileTableMode="scroll"
            pagination={{ pagination, setPagination, totalItems: references.length }}
            sort={{ sorting, setSorting }}
            tableAriaLabel={t("transformation.references.title", "Manage the transformation processes")}
        />
    );
};
